import { Link, router, usePage } from "@inertiajs/react"
import { StaffLayout } from "@/components/layouts/staff-layout"
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog"
import { Button } from "@/components/ui/button"
import { MainContainer } from "@/components/ui/container"
import { DataTable } from "@/components/ui/data-table"
import type { DataColumn } from "@/components/ui/data-table"
import { PageHeader } from "@/components/ui/headers"
import { t } from "@/lib/i18n"

type LocationUser = {
  id: number
  userId: number
  name: string
  email: string
  role: string
  roleLabel: string
}

function destroyUser(userId: number) {
  router.delete(`/manage/users/${userId}`)
}

function DeleteUserDialog({ user }: { user: LocationUser }) {
  return (
    <AlertDialog>
      <AlertDialogTrigger asChild>
        <Button type="button" variant="destructive" size="sm">
          {t("ui.react.pages.staff.location.manage.users.index.delete")}
        </Button>
      </AlertDialogTrigger>
      <AlertDialogContent data-testid={`delete-user-dialog-${user.id}`}>
        <AlertDialogHeader>
          <AlertDialogTitle>
            {t("ui.react.pages.staff.location.manage.users.index.delete_staff_member")}
          </AlertDialogTitle>
          <AlertDialogDescription>
            {t("ui.react.pages.staff.location.manage.users.index.action_remove_access_staff_member")}
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel>
            {t("ui.react.pages.staff.location.manage.users.index.cancel")}
          </AlertDialogCancel>
          <AlertDialogAction
            variant="destructive"
            onClick={() => destroyUser(user.id)}
            data-testid={`confirm-delete-user-${user.id}`}
          >
            {t("ui.react.pages.staff.location.manage.users.index.delete")}
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  )
}

const columns: DataColumn<LocationUser>[] = [
  {
    accessorKey: "name",
    header: t("ui.react.pages.staff.location.manage.users.index.name"),
    field: { type: "title", truncate: true },
  },
  {
    accessorKey: "email",
    header: t("ui.react.pages.staff.location.manage.users.index.email"),
    field: { type: "text", truncate: true },
    mobile: "meta",
  },
  {
    accessorKey: "roleLabel",
    header: t("ui.react.pages.staff.location.manage.users.index.role"),
    field: { type: "text" },
    mobile: "secondary",
  },
  {
    id: "actions",
    header: t("ui.react.pages.staff.location.manage.users.index.actions"),
    enableHiding: false,
    field: {
      type: "actions",
      render: (user) => (
        <>
          <Button asChild variant="outline" size="sm">
            <Link href={`/manage/users/${user.id}/edit`}>
              {t("ui.react.pages.staff.location.manage.users.index.edit")}
            </Link>
          </Button>
          <DeleteUserDialog user={user} />
        </>
      ),
    },
  },
]

export default function StaffLocationManageUsersIndex() {
  const { users } = usePage<{ users: LocationUser[] }>().props

  return (
    <StaffLayout>
      <MainContainer size="md">
        <div className="space-y-6" data-testid="users-management-view">
          <PageHeader
            title={t("ui.react.pages.staff.location.manage.users.index.staff")}
            description={t(
              "ui.react.pages.staff.location.manage.users.index.manage_staff_members_assigned_location"
            )}
            actions={
              <Button asChild data-testid="add-user-button">
                <Link href="/manage/users/new">
                  {t("ui.react.pages.staff.location.manage.users.index.add_staff")}
                </Link>
              </Button>
            }
          />

          {/*
            The heading sits on the paper and the data stands on its own surface
            below it. This used to be a `Card` wrapping a `Table`, which is the
            nested-box shape DESIGN.md's Title-on-Paper Rule exists to prevent.
          */}
          <section className="space-y-3" data-testid="manage-users-card">
            <div className="space-y-1">
              <h2 className="text-base font-semibold tracking-tight">
                {t("ui.react.pages.staff.location.manage.users.index.team_current")}
              </h2>
              <p className="text-muted-foreground text-sm">
                {t("ui.react.pages.staff.location.manage.users.index.active_access_description")}
              </p>
            </div>
            <DataTable
              columns={columns}
              data={users}
              surface="card"
              rowTestId={(user) => `manage-user-row-${user.id}`}
              emptyMessage={t("ui.react.pages.staff.location.manage.users.index.no_users")}
            />
          </section>
        </div>
      </MainContainer>
    </StaffLayout>
  )
}
